import type { ChatMessage, Member, Reaction, ReactionEmoji } from '@sokdaq/protocol';

/**
 * 화면에 그릴 메시지.
 *
 * pending은 서버 확정을 기다리는 낙관적 항목이다. 보낸 즉시 화면에
 * 나타나고, 서버가 같은 id로 돌려주면 seq를 얻으며 확정된다.
 */
export interface DisplayMessage extends ChatMessage {
  readonly pending?: boolean;
}

/**
 * 같은 id가 있으면 교체하고, 없으면 끝에 붙인다.
 *
 * 교체가 필요한 경우가 둘 있다. 낙관적 항목이 서버 확정본으로 바뀔 때,
 * 그리고 재연결 델타에 이미 받은 메시지가 섞여 올 때.
 *
 * 도착 순서를 그대로 표시 순서로 쓴다. 서버가 단일 스레드라 브로드캐스트
 * 순서가 곧 seq 순서이고, welcome의 과거 메시지도 정렬되어 온다.
 * 속닥은 저장되지 않아 seq가 0인데, seq로 정렬하면 그것들이 맨 앞으로
 * 몰린다 — 도착 순서를 쓰면 그 문제가 아예 생기지 않는다.
 */
export function upsertMessage(
  messages: readonly DisplayMessage[],
  incoming: ChatMessage,
): DisplayMessage[] {
  const index = messages.findIndex((m) => m.id === incoming.id);
  if (index === -1) return [...messages, incoming];

  const next = messages.slice();
  next[index] = incoming;
  return next;
}

/** 재접속 델타를 기존 목록에 합친다. */
export function mergeMessages(
  existing: readonly DisplayMessage[],
  delta: readonly ChatMessage[],
): DisplayMessage[] {
  return delta.reduce<DisplayMessage[]>((acc, message) => upsertMessage(acc, message), [
    ...existing,
  ]);
}

export function upsertMember(members: readonly Member[], incoming: Member): Member[] {
  const index = members.findIndex((m) => m.userId === incoming.userId);
  if (index === -1) return [...members, incoming];

  const next = members.slice();
  next[index] = incoming;
  return next;
}

/**
 * 같은 사람이 짧은 간격으로 이어 말하면 머리글을 반복하지 않는다.
 * 속닥은 성격이 다르므로 묶지 않는다.
 */
const GROUPING_WINDOW_MS = 3 * 60 * 1000;

export function isGrouped(
  previous: DisplayMessage | undefined,
  current: DisplayMessage,
): boolean {
  if (!previous) return false;
  if (previous.userId !== current.userId) return false;
  if (previous.kind !== current.kind) return false;
  if (current.kind === 'whisper') return false;
  return current.createdAt - previous.createdAt < GROUPING_WINDOW_MS;
}


// ─── 리액션 ─────────────────────────────────────────────────

/** msgId → 이모지 → 누른 사람들. */
export type ReactionMap = Readonly<Record<string, Readonly<Record<string, readonly string[]>>>>;

export function buildReactionMap(reactions: readonly Reaction[]): ReactionMap {
  const map: Record<string, Record<string, string[]>> = {};
  for (const reaction of reactions) {
    const byEmoji = (map[reaction.msgId] ??= {});
    (byEmoji[reaction.emoji] ??= []).push(reaction.userId);
  }
  return map;
}

/**
 * 리액션 하나를 켜거나 끈다.
 *
 * 원본을 바꾸지 않고 새 객체를 만든다. 리액션이 하나도 남지 않은 항목은
 * 지운다 — 빈 배열을 남겨두면 화면에 개수 0인 칩이 뜬다.
 */
export function applyReaction(
  map: ReactionMap,
  msgId: string,
  emoji: ReactionEmoji,
  userId: string,
  on: boolean,
): ReactionMap {
  const byEmoji = { ...(map[msgId] ?? {}) };
  const users = byEmoji[emoji] ?? [];

  const next = on
    ? users.includes(userId)
      ? users
      : [...users, userId]
    : users.filter((id) => id !== userId);

  if (next.length === 0) delete byEmoji[emoji];
  else byEmoji[emoji] = next;

  const result = { ...map };
  if (Object.keys(byEmoji).length === 0) delete result[msgId];
  else result[msgId] = byEmoji;

  return result;
}

/** 화면에 그릴 순서대로. 많이 눌린 것이 앞에 온다. */
export function reactionChips(
  map: ReactionMap,
  msgId: string,
  myUserId: string | undefined,
): { emoji: string; count: number; mine: boolean }[] {
  const byEmoji = map[msgId];
  if (byEmoji === undefined) return [];

  return Object.entries(byEmoji)
    .map(([emoji, users]) => ({
      emoji,
      count: users.length,
      mine: myUserId !== undefined && users.includes(myUserId),
    }))
    .sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji));
}
